import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthorizationService } from 'src/app/authorization/authorization.service';
import { TitleService } from '../../layout/title.service';
import { LoggerService } from '../../telemetry/logger.service';
import { AuthenticationService } from '../authentication.service';
import { VersionUpdateService } from 'src/app/versioning/version-update-service';
import { DefaultTelemetryProperties } from 'src/app/telemetry/default-telemetry-properties';

/**
 * This component logs a user out of the app.
 */
@Component({
	selector: 'crownx-logout',
	templateUrl: './logout.component.html',
	styleUrls: ['./logout.component.css']
})
export class LogoutComponent implements OnInit, OnDestroy {

	/** Indicates if the authentication service encountered an error. */
	public HasAuthenticationError: boolean = false;

	/** Used to unsubscribe from observables. */
	private unsubscriber = new Subject();

	/**
	 * @constructor
	 *
	 * @param authenticationService The authentication service.
	 * @param router The Angular router service.
	 * @param logger The logging service.
	 * @param titleService The title service.
	 * @param authorizationService The authorization service.
	 * @param versionUpdateService The service used for checking and initiating client updates when an update is available
	 */
	constructor(
		private readonly authenticationService: AuthenticationService,
		private readonly router: Router,
		private readonly logger: LoggerService,
		private readonly titleService: TitleService,
		private readonly authorizationService: AuthorizationService,
		private readonly versionUpdateService: VersionUpdateService
	) { }

	/**
	 * Component initialization.
	 */
	public ngOnInit(): void {
		this.titleService.Title = "Logout";
		this.Logout();
	}

	/**
	 * Component destruction.
	 */
	public ngOnDestroy(): void {
		this.unsubscriber.next(null);
		this.unsubscriber.complete();
	}

	/**
	 * Logs the user out.
	 */
	private Logout(): void {
		// We want to use the cache authentication on logout when there is a version mismatch because the GetAuthentication$ call will be rejected.
		this.authenticationService.GetAuthentication$(!this.versionUpdateService.IsServerRejectingRequests)
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: () => {
					const userName: string | null = this.authenticationService.UserName;
					this.logger.TrackTrace("Logging out user.");
					this.authenticationService.Logout$().pipe(takeUntil(this.unsubscriber))
						.subscribe({
							next: () => {
								const telemetryProperties: Pick<DefaultTelemetryProperties, "Username"> = { Username: userName };
								this.logger.TrackTrace("The user has logged out.", telemetryProperties);
								this.authorizationService.ClearUserAuthorization();
								this.router.navigate(["/login"]);
							},
							error: () => this.HasAuthenticationError = true
						});
				},
				error: (error: Error) => {
					this.HasAuthenticationError = true;
					const telemetryProperties: Pick<DefaultTelemetryProperties, "Username"> = { Username: this.authenticationService.UserName };
					this.logger.TrackError(`Error getting user during logout.`, error, telemetryProperties);
				}
			});
	}
}
